import cn from 'classnames';
import { times } from 'lodash';
import handDrawnOutline from 'src/assets/svg/landing/hand_drawn_outline.svg';
import { PanelCarouselCircleTabs } from 'src/rebrand/Homepage/components/shared/PanelCarouselStyles';
import { MapPin } from 'src/shared/design-system/Icon/Location';
import { useRefState } from 'src/shared/hooks/useRefState';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import RatingStar from 'web-common/src/shared/design-system/icons/RatingStar.svg';
import { useCarousel } from '../hooks/useCarousel';
import { LayoutContainer } from './LayoutContainer';

export const PADDING = 16;
export const M_AND_LG_PADDING = 110;
export const SHADOW_BREATHING_ROOM = 36;
export const IS_ONSCREEN_THRESHOLD = 500; // (Only applies on large screens)

const HOMEPAGE_WIDTH_MEDIUM = 768;
const HOMEPAGE_WIDTH_LARGE = 1080;

const PanelCarouselRow = styled.div`
  width: 100%;

  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  overflow-x: scroll;
  overflow-y: clip;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    display: none;
  }

  scroll-padding-left: ${SHADOW_BREATHING_ROOM}px;
  scroll-padding-right: ${SHADOW_BREATHING_ROOM}px;

  // Give box shadows breathing room at the bottom of this scrollable div
  padding-bottom: 50px;
  margin-bottom: -50px; // Prevent this from affecting anything outside the component
`;

const SizedShefCard = styled.div`
  border-radius: 1.5rem;
  min-width: 326px;
  transition: transform 0.4s ease;

  // Size to have 3 cards in main view (2 for very small tablets)
  @media (min-width: '320px') {
    & {
      min-width: calc((100vw - ${PADDING}px * 3) / 2);
      max-width: calc((100vw - ${PADDING}px * 3) / 2);
    }
  }

  @media (min-width: '390px') {
    & {
      min-width: calc((100vw - ${PADDING}px * 5) / 3);
      max-width: calc((100vw - ${PADDING}px * 5) / 3);
    }
  }

  @media (min-width: '768px') {
    & {
      background-color: green;
      min-width: calc((${HOMEPAGE_WIDTH_MEDIUM}px - ${PADDING}px * 5) / 3);
      max-width: calc((${HOMEPAGE_WIDTH_MEDIUM}px - ${PADDING}px * 5) / 3);
    }
  }

  @media (min-width: '1080px') {
    min-width: calc((${HOMEPAGE_WIDTH_LARGE}px - ${PADDING}px * 5) / 3);
    max-width: calc((${HOMEPAGE_WIDTH_LARGE}px - ${PADDING}px * 5) / 3);
  }
`;

const shefs = [
  {
    name: 'Shef Kimberly',
    region: 'New York',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_kimberly.jpg',
    foodType: 'Vegan Food',
    rating: 4.4,
  },
  {
    name: 'Shef Matthew',
    region: 'New York',
    foodType: 'American Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_matthew.jpg',
    rating: 4.9,
  },
  {
    name: 'Shef Lillian',
    region: 'Bay Area',
    foodType: 'African Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_lillian.jpg',
    rating: 4.8,
  },
  {
    name: 'Shef Davit',
    region: 'Los Angeles',
    foodType: 'Middle Eastern Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_davit.jpg',
    rating: 4.2,
  },
  {
    name: 'Shef Angelina',
    region: 'Los Angeles',
    foodType: 'Caribbean Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_angelina.jpg',
    rating: 4.9,
  },
  {
    name: 'Shef Alexis',
    region: 'Los Angeles',
    foodType: 'Filipino Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_alexis.jpg',
    rating: 4.8,
  },
  {
    name: 'Shef Divya',
    region: 'Los Angeles',
    foodType: 'Indian Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_divya.jpg',
    rating: 4.7,
  },
  {
    name: 'Shef Ricardo',
    region: 'New York City',
    foodType: 'Caribbean Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_ricardo.jpg',
    rating: 4.5,
  },
  {
    name: 'Shef Laurnea',
    region: 'New York City',
    foodType: 'Vegan Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_laurnea.jpg',
    rating: 4.6,
  },
  {
    name: 'Shef Ly',
    region: 'Bay Area',
    foodType: 'Vietnamese Food',
    imageUrl: 'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/landing/homepage_shefs/shef_ly.jpg',
    rating: 4.9,
  },
];

export const ShefCarousel = () => {
  const [ref, setRef] = useRefState<HTMLDivElement>();
  const { windowDimensions } = useScreenContext();
  const { scrollTo, scrolledElement } = useCarousel({
    ref,
    content: shefs,
    padding: windowDimensions.width < 390 ? PADDING : M_AND_LG_PADDING,
    onScreenThreshold: 200,
  });

  return (
    <div className='flex w-full flex-col py-20 md:flex-row'>
      <LayoutContainer className='mb-12 px-4 max-md:mr-5 md:mr-10 md:w-[50%]' noMarginRight>
        <p className='text-[4rem] font-black uppercase leading-[3.8rem] text-eggplant'>
          Bringing the humanity back to mealtime
        </p>
        <p className='mt-4 text-lg leading-8 text-eggplant'>
          Our talented shefs curate their menus, source ingredients locally, and prepare each meal with love and care.
        </p>
      </LayoutContainer>

      <LayoutContainer
        noMarginLeft={windowDimensions.width < 320}
        className={`flex flex-col items-center font-sans md:w-[50%]`}>
        <div className={cn('relative flex w-full flex-row items-start')}>
          <PanelCarouselRow ref={setRef}>
            {shefs.map((shef, idx) => (
              <SizedShefCard key={idx} className={cn('relative h-[410px]')} onClick={() => scrollTo(idx)}>
                <img
                  src={shef?.imageUrl}
                  alt={shef?.name}
                  className={cn(
                    'h-full w-full rounded-3xl object-cover shadow-lg transition duration-75 ease-in md:duration-150',
                    {
                      'scale-75 md:scale-90': scrolledElement !== idx,
                    }
                  )}
                  style={{
                    opacity: 1,
                    transition: 'opacity 0.11s ease-in-out',
                    boxShadow: '10px 16px 30px rgba(0, 0, 0, 0.1)',
                  }}
                />
                {scrolledElement === idx && (
                  <div>
                    <div
                      className={cn(
                        'absolute right-2 top-2 flex flex-row items-center justify-center gap-1 rounded-md bg-sourdough px-2 py-1 text-xs font-semibold text-eggplant opacity-90 shadow-md'
                      )}>
                      <MapPin color='#35081A' />
                      {shef?.region}
                    </div>

                    <div className={cn('absolute bottom-2 left-2')}>
                      <div className={cn('mb-1 text-xl font-bold text-rice')}>{shef?.name}</div>
                      <div className={cn('flex flex-row gap-2')}>
                        <div
                          className={cn(
                            'flex flex-row items-center justify-center gap-1 rounded-md bg-eggplant-50 px-2 py-1 text-xs font-semibold text-eggplant shadow-md'
                          )}>
                          <img src={RatingStar} alt='Rating Star' />
                          {shef?.rating}
                        </div>
                        <div
                          className={cn(
                            'inline-block rounded-md bg-rice px-2 py-1 text-xs font-semibold uppercase text-eggplant shadow-md'
                          )}>
                          {shef?.foodType}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </SizedShefCard>
            ))}
          </PanelCarouselRow>
        </div>
        <PanelCarouselCircleTabs className='z-10 mt-10'>
          {times(shefs.length).map((idx) => (
            <div
              key={idx}
              className={cn(
                'mt-0 flex h-6 w-6 select-none items-center justify-center bg-contain bg-center bg-no-repeat pt-1 text-xl',
                'md:h-8 md:w-8',
                {
                  'text-raspberry': scrolledElement === idx,
                  'text-eggplant': scrolledElement !== idx,
                }
              )}
              style={{ backgroundImage: scrolledElement === idx ? `url(${handDrawnOutline})` : '' }}
              onClick={() => scrollTo(idx)}>
              {idx + 1}
            </div>
          ))}
        </PanelCarouselCircleTabs>
      </LayoutContainer>
    </div>
  );
};
